<template>
  <window :window-id="windowId">
    <window-tab :title="windowTitle">
      <switch-buttons :template-id="templateId"></switch-buttons>
      <div class="form-group form-row">
        <label :for="safeId('title')" class="col-sm-4 col-form-label">Title</label>
        <div class="col-sm-8">
          <input type="text" class="form-control" :id="safeId('title')" v-model.lazy="title">
        </div>
      </div>
      <div class="form-group form-row">
        <label :for="safeId('top')" class="col-sm-4 col-form-label">TOP</label>
        <div class="col-sm-8">
          <input type="number" class="form-control" :id="safeId('top')" v-model.number.lazy="top">
        </div>
      </div>
      <div class="form-group form-row">
        <label :for="safeId('minutes')" class="col-sm-4 col-form-label">Minutes</label>
        <div class="col-sm-8">
          <input type="number" class="form-control" :id="safeId('minutes')" v-model.number.lazy="minutes">
        </div>
      </div>
      <div class="form-group form-row">
        <label :for="safeId('record')" class="col-sm-4 col-form-label">Athlete</label>
        <div class="col-sm-8">
          <record-select :id="safeId('record')" v-model="recordId" :dataset-id="datasetId"></record-select>
        </div>
      </div>
      <div class="form-group form-row">
        <label :for="safeId('bibs')" class="col-sm-4 col-form-label">BIBs</label>
        <div class="col-sm-8">
          <input type="text" class="form-control" :id="safeId('bibs')" v-model.lazy="bibs">
        </div>
      </div>
    </window-tab>
    <window-tab title="Data" icon="database">
      <album-select-group v-model="albumId"></album-select-group>
      <div class="form-group form-row">
        <label :for="safeId('datasetId')" class="col-sm-4 col-form-label">Dataset</label>
        <div class="col-sm-8">
          <dataset-select :id="safeId('datasetId')" v-model="datasetId"></dataset-select>
        </div>
      </div>
      <div class="form-group form-row" v-for="(v, k) in columnDefinitions" :key="k">
        <label :for="safeId(k)" class="col-sm-4 col-form-label">{{ v }}</label>
        <div class="col-sm-8">
          <column-select :id="safeId(k)" :value="columnMap[k]" @input="val => {
            columnMap[k] = val
            columnMap = columnMap
          }" :dataset-id="datasetId"></column-select>
        </div>
      </div>
    </window-tab>
    <window-tab title="Settings" icon="cog">
      <template-settings :templateId="templateId"></template-settings>
    </window-tab>
  </window>
</template>

<script>
  import { templateMixin } from '@/mixins'
  import { compare, rank, mapOverlayData, mapTemplateData } from '@/utils'
  import * as action from '@/store/action-types'

  export default {
    name: 'intermediate-chart',
    mixins: [templateMixin],
    data () {
      return {
        localTop: 15,
        localMinutes: 10,
        columnDefinitions: {
          'bibNumber': 'Bib number',
          'lastName': 'Last name',
          'firstName': 'First name',
          'country': 'Country',
          'score': 'Score',
          'endTime': 'Time'
        }
      }
    },
    computed: {
      ...mapOverlayData(['title']),
      ...mapOverlayData({
        'records': {default: []},
        'minutes': {default: 10},
        'top': {default: 15}
      }),
      ...mapTemplateData({
        'albumId': {},
        'datasetId': {},
        'recordId': {},
        'columnMap': {default: {}},
        'bibs': {default: ''}
      }),
      dataset () {
        if (!this.datasetId) return null
        return this.$store.getters.dataset(this.datasetId)
      },
      filtered () {
        const dataset = this.dataset
        if (!dataset) return []
        const bibs = this.bibs.split(',')
        return dataset.records
          .filter(r => r.recordId === this.recordId || (bibs.length === 0 || bibs.find(bib => bib === r[this.columnMap['bibNumber']])))
      },
      records () {
        const records = [].concat(this.filtered
          .reduce((result, record) => {
            const mapped = this.mapRecord(record, this.columnMap)
            const data = [{y: 0, x: 0}]
            for (let i = 1; i <= this.localTop; i++) {
              const key = '' + i
              if (record.hasOwnProperty(key) || record[key]) {
                const values = record[key].split(':')
                const value = parseInt(values[0]) * 60 + parseInt(values[1])
                if (value) {
                  data.push({y: i, x: value})
                }
              }
            }
            mapped.active = record.recordId === this.recordId
            if (mapped.score && mapped.score.indexOf('TOP') >= 0) {
              const m = mapped.score.match(/TOP\((.+)\)/)
              if (m) {
                const timeLeftParts = m[1].split('.')
                const x = this.localMinutes * 60 - parseInt(timeLeftParts[0]) * 60 - parseInt(timeLeftParts[1])
                const point = {y: this.localTop, x}
                data.push(point)
              }
            } else if (mapped.endTime) {
              const endTimeParts = mapped.endTime.split(':')
              const x = parseInt(endTimeParts[0]) * 60 + parseInt(endTimeParts[1])
              const point = {y: parseFloat(mapped.score), x}
              data.push(point)
            }
            mapped.data = data
            result.push(mapped)
            return result
          }, []))
          .filter(record => record.score)
        records.sort((a, b) => {
          return compare.uiaaLeadScore(a['score'], b['score'])
        })
        records.forEach(rank('rank', 'score'))
        // console.log(JSON.stringify(records))
        return records.slice(0, 16)
      }
    },
    mounted () {
      this.localTop = this.top
      this.localMinutes = this.minutes
    },
    watch: {
      top: function (val) {
        this.localTop = val
      },
      minutes: function (val) {
        this.localMinutes = val
      },
      records: function (val) {
        // console.log('chart: watched records, dispatching template_overlay_data', val)
        this.$store.dispatch(action.TEMPLATE_OVERLAY_DATA, {
          templateId: this.templateId,
          data: {records: val}
        })
      },
      albumId: function (val) {
        const album = this.$store.getters.album(this.albumId) || {}
        this.$store.dispatch(action.TEMPLATE_OVERLAY_DATA, {
          templateId: this.templateId,
          data: {sponsors: album.pictures}
        })
      }
    },
    methods: {
      mapRecord (record, columnMap) {
        return Object.keys(columnMap).reduce((result, key) => {
          result[key] = record[columnMap[key]]
          return result
        }, {})
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/templates/uiaa-lead-intermediate-chart.vue